import type {
  AdminUser,
  AdminUserList,
  ApiErrorBody,
  AuditList,
  AuditQuery,
  AuthConfig,
  Capacity,
  ConsoleKeyCreate,
  ConsoleKeyList,
  CpuRun,
  CpuRunCreate,
  CpuRunList,
  CpuRunListQuery,
  CpuRunQuery,
  CpuRunTaskList,
  CpuRunTaskQuery,
  CpuWorkspace,
  CpuWorkspaceCreate,
  CpuWorkspaceList,
  CpuWorkspaceListQuery,
  CreatedConsoleKey,
  DisableRequest,
  Entitlement,
  EnvironmentCreate,
  EnvironmentList,
  EnvironmentOption,
  EnvironmentUpdate,
  ExtendRequest,
  Job,
  JobCreate,
  JobList,
  JobListQuery,
  Me,
  MeUpdate,
  NodeRequest,
  NodeRequestResult,
  PauseRequest,
  PauseState,
  Relay,
  ResizeRequest,
  ShellUpdate,
  ShortenRequest,
  MyUsage,
  UsageReport,
  Workspace,
  WorkspaceCreate,
  WorkspaceList,
  WorkspaceListQuery,
} from "./types";

/** Base path of the versioned API, matching `docs/API.md`. */
export const DEFAULT_BASE_URL = "/api/v1";

/**
 * Header every mutating request carries (`docs/AUTH.md` §1).
 *
 * The session is a cookie, so a cross-site form could otherwise post as the
 * researcher. A custom header cannot be set by such a form without CORS, which
 * the server does not grant — belt and braces on top of `SameSite=Lax`.
 */
export const CSRF_HEADER = "X-GPU-Console";

/** Methods that change something and therefore need the CSRF header. */
const MUTATING_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

/** Error raised for every non-2xx API response. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Record<string, unknown> | null;

  constructor(
    status: number,
    code: string,
    message: string,
    details: Record<string, unknown> | null = null,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /** The resource does not exist (e.g. you have no workspace right now). */
  get isNotFound(): boolean {
    return this.status === 404;
  }

  /** The console has no cluster configured — nothing can be measured. */
  get isNotConnected(): boolean {
    return this.status === 503 || this.code === "not_connected";
  }

  /** The cluster or the Scaleway API is configured but refused/failed. */
  get isClusterUnavailable(): boolean {
    return (
      this.status === 502 ||
      this.code === "cluster_unavailable" ||
      this.code === "scaleway_error"
    );
  }

  /** There is no session, or it expired: signing in again is the fix. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  /**
   * Authenticated, but not allowed to do this — somebody else's workspace, or
   * an account without the console's role. Signing in again would not help, so
   * the interface must never offer it as the fix.
   */
  get isForbidden(): boolean {
    return this.status === 403;
  }

  /** Signed in, but nobody has approved this account yet. */
  get isAccountPending(): boolean {
    return this.status === 403 && this.code === "account_pending";
  }

  /** Signed in, but the account has been turned off. */
  get isAccountDisabled(): boolean {
    return this.status === 403 && this.code === "account_disabled";
  }

  // There is deliberately no `isNotEntitled`. `403 not_entitled` is the
  // refusal that would belong to the Jean Zay entitlement, and no route in
  // `docs/API.md` stands behind it: *Training elsewhere* belongs to every
  // approved researcher and its Jean Zay half is omitted from the payload
  // rather than refused. A client path for a code nothing returns is dead
  // code that teaches the next reader the wrong model.
}

type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

interface RequestOptions {
  method?: HttpMethod;
  /** Serialized as JSON when present. */
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  signal?: AbortSignal;
}

/**
 * What the server said about *who* is calling, rather than about the request.
 *
 * - `unauthenticated`: no session at all → the sign-in screen.
 * - `account_blocked`: a real session whose account may not act yet (pending)
 *   or any more (disabled) → the account screen, after re-reading `/me`.
 */
export type AuthEvent = "unauthenticated" | "account_blocked";

export interface ApiClientOptions {
  /** Base URL for the versioned API. */
  baseUrl?: string;
  /**
   * Extra identity headers, resolved per request.
   *
   * Empty with Google sign-in: the identity is an HttpOnly cookie the browser
   * attaches itself. It only ever carries `X-User` in the server's documented
   * development mode, which is not authentication and says so on screen.
   */
  identityHeaders?: () => Record<string, string>;
  /** Notified whenever the server refuses the caller rather than the call. */
  onAuthEvent?: (event: AuthEvent) => void;
  /** Injectable for tests. */
  fetchImpl?: typeof fetch;
}

/** Typed HTTP client for the GPU Console API (`docs/API.md`). */
export interface ApiClient {
  getAuthConfig(signal?: AbortSignal): Promise<AuthConfig>;
  /** Clear the session cookie; the caller then reloads the application. */
  logout(): Promise<void>;
  getCapacity(signal?: AbortSignal): Promise<Capacity>;
  startNodes(payload: NodeRequest): Promise<NodeRequestResult>;
  createWorkspace(payload: WorkspaceCreate): Promise<Workspace>;
  listWorkspaces(
    query?: WorkspaceListQuery,
    signal?: AbortSignal,
  ): Promise<WorkspaceList>;
  getWorkspace(id: string, signal?: AbortSignal): Promise<Workspace>;
  extendWorkspace(id: string, payload: ExtendRequest): Promise<Workspace>;
  /** Bring the deadline in; the workspace keeps running until the new one. */
  shortenWorkspace(id: string, payload: ShortenRequest): Promise<Workspace>;
  /** Take more GPUs or give some back; the environment restarts, files stay. */
  resizeWorkspace(id: string, payload: ResizeRequest): Promise<Workspace>;
  /** The API may answer with the stopped workspace or with no body. */
  stopWorkspace(id: string): Promise<Workspace | null>;
  getWorkspaceLogs(
    id: string,
    tail?: number,
    signal?: AbortSignal,
  ): Promise<string>;
  /** Built-in and saved environments, one list, each saying who may edit it. */
  listEnvironments(signal?: AbortSignal): Promise<EnvironmentList>;
  /** Save an environment: a name over a container image reference. */
  createEnvironment(payload: EnvironmentCreate): Promise<EnvironmentOption>;
  /** Change a saved environment you own. */
  updateEnvironment(
    id: string,
    payload: EnvironmentUpdate,
  ): Promise<EnvironmentOption>;
  /** Delete a saved environment you own. */
  deleteEnvironment(id: string): Promise<void>;
  createJob(payload: JobCreate): Promise<Job>;
  listJobs(query?: JobListQuery, signal?: AbortSignal): Promise<JobList>;
  getJob(id: string, signal?: AbortSignal): Promise<Job>;
  getJobLogs(id: string, tail?: number, signal?: AbortSignal): Promise<string>;
  cancelJob(id: string): Promise<Job | null>;
  /** Submit a CPU run: a command, a size in cores, a deadline. */
  createCpuRun(payload: CpuRunCreate): Promise<CpuRun>;
  listCpuRuns(
    query?: CpuRunListQuery,
    signal?: AbortSignal,
  ): Promise<CpuRunList>;
  /**
   * One run, whole — or with its trace narrowed to what is new.
   *
   * `query.events_after` asks for the events after an index already held; the
   * counters, the state and the deadline are the current ones either way.
   */
  getCpuRun(
    id: string,
    query?: CpuRunQuery,
    signal?: AbortSignal,
  ): Promise<CpuRun>;
  /**
   * One page of a run's tasks — the console's own record, not the cluster's.
   *
   * It keeps answering after the machines are gone: which tasks failed, on
   * which machine, with which exit code, is exactly what somebody comes back
   * to the morning after.
   */
  listCpuRunTasks(
    id: string,
    query?: CpuRunTaskQuery,
    signal?: AbortSignal,
  ): Promise<CpuRunTaskList>;
  /**
   * The log of **one task** of a run.
   *
   * There is no merged log: interleaving a thousand tasks would produce a file
   * in which no single task's output is readable.
   */
  getCpuRunLogs(
    id: string,
    tail?: number,
    task?: number,
    signal?: AbortSignal,
  ): Promise<string>;
  /** Stop every task of a run; the run ends `cancelled`. */
  cancelCpuRun(id: string): Promise<CpuRun | null>;
  /** Push a run's deadline, capped at `MAX_RUN_HOURS` in total. */
  extendCpuRun(id: string, payload: ExtendRequest): Promise<CpuRun>;
  /** Open an interactive CPU workspace: cores, a deadline and an SSH door. */
  createCpuWorkspace(payload: CpuWorkspaceCreate): Promise<CpuWorkspace>;
  listCpuWorkspaces(
    query?: CpuWorkspaceListQuery,
    signal?: AbortSignal,
  ): Promise<CpuWorkspaceList>;
  getCpuWorkspace(id: string, signal?: AbortSignal): Promise<CpuWorkspace>;
  /** Push its deadline, capped at `MAX_WORKSPACE_HOURS` in total. */
  extendCpuWorkspace(
    id: string,
    payload: ExtendRequest,
  ): Promise<CpuWorkspace>;
  /** The API may answer with the stopped workspace or with no body. */
  stopCpuWorkspace(id: string): Promise<CpuWorkspace | null>;
  getCpuWorkspaceLogs(
    id: string,
    tail?: number,
    signal?: AbortSignal,
  ): Promise<string>;
  // There is deliberately no `resizeCpuWorkspace`: that route does not exist
  // and answers 404. To change the size, stop it and start another one with
  // more cores (ADR-0015 decision 7) — the screen says so in those words.
  getMe(signal?: AbortSignal): Promise<Me>;
  updateMe(payload: MeUpdate): Promise<Me>;
  updateShell(payload: ShellUpdate): Promise<Me>;
  /** *Training elsewhere*: every approved researcher, no second permission. */
  getRelay(signal?: AbortSignal): Promise<Relay>;
  /** The console keys that can reach this account right now, newest first. */
  listConsoleKeys(signal?: AbortSignal): Promise<ConsoleKeyList>;
  /**
   * Mint a console key for the command line.
   *
   * The answer is the only place its secret ever exists outside the
   * researcher's own terminal: it is shown once and never stored here.
   */
  createConsoleKey(payload: ConsoleKeyCreate): Promise<CreatedConsoleKey>;
  /** Revoke one of your own keys; it is dead at its next request. */
  revokeConsoleKey(id: string): Promise<void>;
  listAccounts(signal?: AbortSignal): Promise<AdminUserList>;
  /** What every person consumed over the last `days` days. */
  getUsage(days: number, signal?: AbortSignal): Promise<UsageReport>;
  /** Your own consumption over the last `days` days, priced when it can be. */
  getMyUsage(days: number, signal?: AbortSignal): Promise<MyUsage>;
  approveAccount(username: string): Promise<AdminUser | null>;
  disableAccount(
    username: string,
    payload: DisableRequest,
  ): Promise<AdminUser | null>;
  /** Record that the team has declared an account to IDRIS. Idempotent. */
  grantJeanZay(username: string): Promise<Entitlement>;
  /** Take the record back — the row is stamped, never deleted. Idempotent. */
  revokeJeanZay(username: string): Promise<Entitlement>;
  listAllWorkspaces(signal?: AbortSignal): Promise<WorkspaceList>;
  setPaused(payload: PauseRequest): Promise<PauseState | null>;
  getAuditLog(query?: AuditQuery, signal?: AbortSignal): Promise<AuditList>;
  /**
   * Where the browser goes to download every matching audit row as CSV.
   *
   * A navigation, not a fetch: the session cookie authenticates it and the
   * file lands in the downloads folder like any attachment.
   */
  auditExportUrl(query?: AuditQuery): string;
}

function buildUrl(
  baseUrl: string,
  path: string,
  query: RequestOptions["query"],
): string {
  const url = `${baseUrl}${path}`;
  if (!query) {
    return url;
  }
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined) {
      params.set(key, String(value));
    }
  }
  const serialized = params.toString();
  return serialized ? `${url}?${serialized}` : url;
}

function isErrorBody(value: unknown): value is ApiErrorBody {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const candidate = value as Partial<ApiErrorBody>;
  return (
    typeof candidate.error === "string" && typeof candidate.message === "string"
  );
}

async function toApiError(response: Response): Promise<ApiError> {
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    body = null;
  }
  if (isErrorBody(body)) {
    return new ApiError(
      response.status,
      body.error,
      body.message,
      body.details ?? null,
    );
  }
  return new ApiError(
    response.status,
    "http_error",
    `The server answered ${response.status} ${response.statusText}`.trim(),
  );
}

/**
 * Create a typed API client.
 *
 * Every call sends the session cookie (`credentials: "include"`), and every
 * mutation sends the CSRF header the API requires. Any non-2xx answer becomes
 * an `ApiError`; the two that are about the caller rather than the call — no
 * session, or an account that may not act — are also reported through
 * `onAuthEvent` so the application can drop to the right screen from anywhere.
 *
 * Args:
 *     options: Base URL, identity-header resolver, authentication-event
 *         callback and optional `fetch` override used by tests.
 *
 * Returns:
 *     An `ApiClient` raising `ApiError` on any non-2xx response.
 */
export function createApiClient(options: ApiClientOptions = {}): ApiClient {
  const baseUrl = options.baseUrl ?? DEFAULT_BASE_URL;
  const doFetch = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
  const identityHeaders = options.identityHeaders ?? ((): Record<string, string> => ({}));

  function report(error: ApiError): void {
    if (!options.onAuthEvent) {
      return;
    }
    if (error.isUnauthenticated) {
      options.onAuthEvent("unauthenticated");
    } else if (error.isAccountPending || error.isAccountDisabled) {
      options.onAuthEvent("account_blocked");
    }
  }

  async function request(
    path: string,
    { method = "GET", body, query, signal }: RequestOptions = {},
  ): Promise<Response> {
    const headers: Record<string, string> = {
      Accept: "application/json",
      ...identityHeaders(),
    };
    if (MUTATING_METHODS.has(method)) {
      headers[CSRF_HEADER] = "1";
    }
    if (body !== undefined) {
      headers["Content-Type"] = "application/json";
    }
    const init: RequestInit = { method, headers, credentials: "include" };
    if (body !== undefined) {
      init.body = JSON.stringify(body);
    }
    if (signal) {
      init.signal = signal;
    }
    const response = await doFetch(buildUrl(baseUrl, path, query), init);
    if (!response.ok) {
      const error = await toApiError(response);
      report(error);
      throw error;
    }
    return response;
  }

  async function requestJson<T>(
    path: string,
    init?: RequestOptions,
  ): Promise<T> {
    const response = await request(path, init);
    return (await response.json()) as T;
  }

  /** Tolerates the documented "no body" answer of DELETE/action routes. */
  async function requestOptionalJson<T>(
    path: string,
    init?: RequestOptions,
  ): Promise<T | null> {
    const response = await request(path, init);
    const text = await response.text();
    return text.trim() ? (JSON.parse(text) as T) : null;
  }

  return {
    getAuthConfig: (signal) => requestJson<AuthConfig>("/auth/config", { signal }),

    logout: async () => {
      await request("/auth/logout", { method: "POST" });
    },

    getCapacity: (signal) => requestJson<Capacity>("/capacity", { signal }),

    startNodes: (payload) =>
      requestJson<NodeRequestResult>("/nodes", {
        method: "POST",
        body: payload,
      }),

    createWorkspace: (payload) =>
      requestJson<Workspace>("/workspaces", { method: "POST", body: payload }),

    listWorkspaces: (query, signal) =>
      requestJson<WorkspaceList>("/workspaces", {
        query: {
          mine: query?.mine,
          state: query?.state,
          limit: query?.limit,
        },
        signal,
      }),

    getWorkspace: (id, signal) =>
      requestJson<Workspace>(`/workspaces/${encodeURIComponent(id)}`, {
        signal,
      }),

    extendWorkspace: (id, payload) =>
      requestJson<Workspace>(`/workspaces/${encodeURIComponent(id)}/extend`, {
        method: "POST",
        body: payload,
      }),

    shortenWorkspace: (id, payload) =>
      requestJson<Workspace>(`/workspaces/${encodeURIComponent(id)}/shorten`, {
        method: "POST",
        body: payload,
      }),

    resizeWorkspace: (id, payload) =>
      requestJson<Workspace>(`/workspaces/${encodeURIComponent(id)}/resize`, {
        method: "POST",
        body: payload,
      }),

    stopWorkspace: (id) =>
      requestOptionalJson<Workspace>(`/workspaces/${encodeURIComponent(id)}`, {
        method: "DELETE",
      }),

    getWorkspaceLogs: async (id, tail = 200, signal) => {
      const response = await request(
        `/workspaces/${encodeURIComponent(id)}/logs`,
        { query: { tail }, signal },
      );
      return response.text();
    },

    listEnvironments: (signal) =>
      requestJson<EnvironmentList>("/environments", { signal }),

    createEnvironment: (payload) =>
      requestJson<EnvironmentOption>("/environments", {
        method: "POST",
        body: payload,
      }),

    updateEnvironment: (id, payload) =>
      requestJson<EnvironmentOption>(
        `/environments/${encodeURIComponent(id)}`,
        { method: "PATCH", body: payload },
      ),

    deleteEnvironment: async (id) => {
      await request(`/environments/${encodeURIComponent(id)}`, {
        method: "DELETE",
      });
    },

    createJob: (payload) =>
      requestJson<Job>("/jobs", { method: "POST", body: payload }),

    listJobs: (query, signal) =>
      requestJson<JobList>("/jobs", {
        query: { mine: query?.mine, state: query?.state, limit: query?.limit },
        signal,
      }),

    getJob: (id, signal) =>
      requestJson<Job>(`/jobs/${encodeURIComponent(id)}`, { signal }),

    getJobLogs: async (id, tail = 500, signal) => {
      const response = await request(`/jobs/${encodeURIComponent(id)}/logs`, {
        query: { tail },
        signal,
      });
      return response.text();
    },

    cancelJob: (id) =>
      requestOptionalJson<Job>(`/jobs/${encodeURIComponent(id)}/cancel`, {
        method: "POST",
      }),

    createCpuRun: (payload) =>
      requestJson<CpuRun>("/cpu-runs", { method: "POST", body: payload }),

    listCpuRuns: (query, signal) =>
      requestJson<CpuRunList>("/cpu-runs", {
        query: { mine: query?.mine, state: query?.state, limit: query?.limit },
        signal,
      }),

    getCpuRun: (id, query, signal) =>
      requestJson<CpuRun>(`/cpu-runs/${encodeURIComponent(id)}`, {
        query: { events_after: query?.events_after },
        signal,
      }),

    listCpuRunTasks: (id, query, signal) =>
      requestJson<CpuRunTaskList>(`/cpu-runs/${encodeURIComponent(id)}/tasks`, {
        query: {
          state: query?.state,
          after: query?.after,
          limit: query?.limit,
        },
        signal,
      }),

    getCpuRunLogs: async (id, tail = 500, task = 0, signal) => {
      const response = await request(
        `/cpu-runs/${encodeURIComponent(id)}/logs`,
        { query: { tail, task }, signal },
      );
      return response.text();
    },

    cancelCpuRun: (id) =>
      requestOptionalJson<CpuRun>(`/cpu-runs/${encodeURIComponent(id)}/cancel`, {
        method: "POST",
      }),

    extendCpuRun: (id, payload) =>
      requestJson<CpuRun>(`/cpu-runs/${encodeURIComponent(id)}/extend`, {
        method: "POST",
        body: payload,
      }),

    createCpuWorkspace: (payload) =>
      requestJson<CpuWorkspace>("/cpu-workspaces", {
        method: "POST",
        body: payload,
      }),

    listCpuWorkspaces: (query, signal) =>
      requestJson<CpuWorkspaceList>("/cpu-workspaces", {
        query: { mine: query?.mine, state: query?.state, limit: query?.limit },
        signal,
      }),

    getCpuWorkspace: (id, signal) =>
      requestJson<CpuWorkspace>(`/cpu-workspaces/${encodeURIComponent(id)}`, {
        signal,
      }),

    extendCpuWorkspace: (id, payload) =>
      requestJson<CpuWorkspace>(
        `/cpu-workspaces/${encodeURIComponent(id)}/extend`,
        { method: "POST", body: payload },
      ),

    stopCpuWorkspace: (id) =>
      requestOptionalJson<CpuWorkspace>(
        `/cpu-workspaces/${encodeURIComponent(id)}`,
        { method: "DELETE" },
      ),

    getCpuWorkspaceLogs: async (id, tail = 200, signal) => {
      const response = await request(
        `/cpu-workspaces/${encodeURIComponent(id)}/logs`,
        { query: { tail }, signal },
      );
      return response.text();
    },

    getMe: (signal) => requestJson<Me>("/me", { signal }),

    updateMe: (payload) =>
      requestJson<Me>("/me", { method: "PUT", body: payload }),

    updateShell: (payload) =>
      requestJson<Me>("/me/shell", { method: "PUT", body: payload }),

    getRelay: (signal) => requestJson<Relay>("/relay", { signal }),

    listConsoleKeys: (signal) =>
      requestJson<ConsoleKeyList>("/me/keys", { signal }),

    createConsoleKey: (payload) =>
      requestJson<CreatedConsoleKey>("/me/keys", {
        method: "POST",
        body: payload,
      }),

    revokeConsoleKey: async (id) => {
      await request(`/me/keys/${encodeURIComponent(id)}`, { method: "DELETE" });
    },

    listAccounts: (signal) =>
      requestJson<AdminUserList>("/admin/users", { signal }),

    getUsage: (days, signal) =>
      requestJson<UsageReport>("/admin/usage", { query: { days }, signal }),
    getMyUsage: (days, signal) =>
      requestJson<MyUsage>("/me/usage", { query: { days }, signal }),

    approveAccount: (username) =>
      requestOptionalJson<AdminUser>(
        `/admin/users/${encodeURIComponent(username)}/approve`,
        { method: "POST" },
      ),

    disableAccount: (username, payload) =>
      requestOptionalJson<AdminUser>(
        `/admin/users/${encodeURIComponent(username)}/disable`,
        { method: "POST", body: payload },
      ),

    // The capability is spelled `jean-zay` in the path and `jean_zay` in the
    // answer it carries (`docs/API.md`). Neither call changes who may log into
    // the machine: that list is every `active` account with a key.
    grantJeanZay: (username) =>
      requestJson<Entitlement>(
        `/admin/users/${encodeURIComponent(username)}/entitlements/jean-zay`,
        { method: "POST" },
      ),

    revokeJeanZay: (username) =>
      requestJson<Entitlement>(
        `/admin/users/${encodeURIComponent(username)}/entitlements/jean-zay`,
        { method: "DELETE" },
      ),

    listAllWorkspaces: (signal) =>
      requestJson<WorkspaceList>("/admin/workspaces", { signal }),

    setPaused: (payload) =>
      requestOptionalJson<PauseState>("/admin/pause", {
        method: "POST",
        body: payload,
      }),

    getAuditLog: (query = {}, signal) =>
      requestJson<AuditList>("/admin/audit", { query: { ...query }, signal }),
    auditExportUrl: (query = {}) => {
      const { limit: _limit, offset: _offset, ...filters } = query;
      return buildUrl(baseUrl, "/admin/audit/export", { ...filters });
    },
  };
}
